import { Injectable } from '@angular/core';
import { ValidatorService } from 'src/app/core/validator/validator.service';
import { StockRevaluation } from './stock-revaluation.model';
import { StockQuantityService } from '../../services/stock-quantity.service';

@Injectable({
  providedIn: 'root'
})
export class StockRevaluationValidatorService extends ValidatorService {

  constructor(private stockQuantityService: StockQuantityService) {
    super();
  }

  /**
   * A revaluation needs a reference, and at least one line carrying a product, a location and a
   * stated value that is not negative.
   *
   * There is no quantity check because there is no quantity. That is the whole shape of this
   * operation, and it is why the four stock operations keep their own validator rather than this one
   * relaxing theirs.
   */
  isValid(data: StockRevaluation) {
    this.errors = [];

    if (!this.isDropdownSupplied(data.referenceId)) {
      this.errors.push('referenceId');
    }

    if (!data.lines || data.lines.length === 0) {
      this.errors.push('lines');
      return false;
    }

    data.lines.forEach((line, index) => {

      if (!this.isDropdownSupplied(line.productId)) {
        this.errors.push('productId-' + index);
      }

      // Required, and recorded, because an operator writing stock down is stating which stock they
      // were looking at. It does not scope the money to that shelf - the average is per product.
      if (!this.isDropdownSupplied(line.warehouseLocationId)) {
        this.errors.push('warehouseLocationId-' + index);
      }

      // Zero is NOT refused here, and the rule this replaces said the opposite. A stated value of
      // zero is a real answer - stock condemned or written off entirely is worth nothing - so the
      // only thing left to check is that an amount was stated at all and that it is not negative.
      //
      // Which is why "supplied" is tested separately from the amount rather than folded into it: an
      // untouched field arrives as null and a deliberate write-off arrives as 0, and those two must
      // not collapse into one another. Number.isFinite catches the NaN an unparseable entry gives,
      // which isSupplied alone would pass.
      if (!this.isSupplied(line.targetValue) || !Number.isFinite(Number(line.targetValue))) {
        this.errors.push('targetValue-' + index);
      } else if (this.stockQuantityService.isNegative(line.targetValue)) {
        // Stock cannot be worth less than nothing. The backend refuses this too; it is refused here
        // as well so the operator is told before posting rather than by a 400 afterwards.
        this.errors.push('targetValue-' + index);
      }
    });

    return this.errors.length === 0;
  }

  hasError(field: string): boolean {
    return this.errors.indexOf(field) !== -1;
  }
}
